import { Fragment, FC, useState, useEffect } from 'react';

import { Button, Flex, Select, Spin, Typography } from 'antd';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ComposedChart,
  ResponsiveContainer,
} from 'recharts';
import { Payload } from 'recharts/types/component/DefaultTooltipContent';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import IconDownload from '@/assets/icons/icon-download.svg?react';
import IconError from '@/assets/icons/icon-error.svg?react';
import IconInfo from '@/assets/icons/icon-info.svg?react';
import Card from '@/components/RCard';
import CustomGraphicTooltip from '@/components/RGraphicTooltip';
import RToggleButtons from '@/components/RToggleButtons';
import RTooltip from '@/components/RTooltip';
import useCurrency from '@/contexts/useCurrency';
import {
  SimulationResult,
  SimulationResultYearlyFinancialAnalysis,
  SimulationResultYearlyFinancialAnalysisData,
} from '@/types/simulations';
import { formatNumberWithCommas, getTickStep, tickFormatter } from '@/utils';

import StyledCashFlow from './styles';

type CashFlowProps = {
  barWidth?: number;
  activeTab?: string;
  simulationId?: number;
  activeButton?: number;
};

const CashFlow: FC<CashFlowProps> = ({
  activeTab,
  simulationId,
  activeButton,
  barWidth = 16,
}) => {
  const { currencySymbol } = useCurrency();
  const { t } = useTranslation();
  const [cashFlowType, setCashFlowType] = useState('freeCashFlowToFirm');
  const [storageSelection, setStorageSelection] = useState('withStorage');
  const params = useParams<Record<string, string>>();
  const resultId = simulationId || params.resultId;
  const {
    data: financialData,
    isLoading,
    error,
  } = useSWR<SimulationResultYearlyFinancialAnalysis>(
    `/simulation/${resultId}/yearly_financial_analysis/`,
    {
      revalidateOnFocus: false,
    }
  );
  const isDataZero = financialData?.graphData.length === 0;

  const { data: simulationData } = useSWR<SimulationResult>(
    `/simulation/${resultId}/`,
    {
      revalidateOnFocus: false,
    }
  );
  const [closedDataKeys, setClosedDataKeys] = useState<string[]>([]);
  const [selectedSwitch, setSelectedSwitch] = useState('Nominal');
  const theme = useTheme();

  useEffect(() => {
    if (activeTab) {
      setStorageSelection(activeTab);
    }
    if (activeButton) {
      setSelectedSwitch(activeButton === 0 ? 'Nominal' : 'NPV');
    }
  }, [activeTab, activeButton]);

  const handleCashFlowTypeSelection = (value: string) => {
    setCashFlowType(value);
  };

  const handleStorageSelection = (value: string) => {
    setStorageSelection(value);
  };

  const getStorageSpecificData = (
    item: SimulationResultYearlyFinancialAnalysisData
  ) => {
    const isNPV = selectedSwitch === 'NPV';

    const dataMap: Record<
      string,
      {
        netCashFlow: number;
        cumulativeCashFlow: number;
        capex: number;
      }
    > = {
      'withStorage-freeCashFlowToFirm': {
        netCashFlow: isNPV
          ? item.npvOfFreeCashFlowToFirm || 0
          : item.freeCashFlowToFirm || 0,
        cumulativeCashFlow: isNPV
          ? item.cumulativeNpvOfFreeCashFlowToFirm || 0
          : item.cumulativeFreeCashFlowToFirm || 0,
        capex: isNPV
          ? item.netCapexWithStorage || 0
          : item.capexWithStorage || 0,
      },
      'withStorage-freeCashFlowToEquity': {
        netCashFlow: isNPV
          ? item.npvOfFreeCashFlowToEquity || 0
          : item.freeCashFlowToEquity || 0,
        cumulativeCashFlow: isNPV
          ? item.cumulativeNpvOfFreeCashFlowToEquity || 0
          : item.cumulativeFreeCashFlowToEquity || 0,
        capex: isNPV
          ? item.netCapexWithStorage || 0
          : item.capexWithStorage || 0,
      },
      'withoutStorage-freeCashFlowToFirm': {
        netCashFlow: isNPV
          ? item.npvOfFreeCashFlowToFirmOfWos || 0
          : item.freeCashFlowToFirmOfWos || 0,
        cumulativeCashFlow: isNPV
          ? item.cumulativeNpvOfFreeCashFlowToFirmOfWos || 0
          : item.cumulativeFreeCashFlowToFirmOfWos || 0,
        capex: isNPV
          ? item.netCapexWithoutStorage || 0
          : item.capexWithoutStorage || 0,
      },
      'withoutStorage-freeCashFlowToEquity': {
        netCashFlow: isNPV
          ? item.npvOfFreeCashFlowToEquityWos || 0
          : item.freeCashFlowToEquityWos || 0,
        cumulativeCashFlow: isNPV
          ? item.cumulativeNpvOfFreeCashFlowToEquityWos || 0
          : item.cumulativeFreeCashFlowToEquityWos || 0,
        capex: isNPV
          ? item.netCapexWithoutStorage || 0
          : item.capexWithoutStorage || 0,
      },
      'storage-freeCashFlowToFirm': {
        netCashFlow: isNPV
          ? item.npvOfFreeCashFlowToFirmOfStorage || 0
          : item.freeCashFlowToFirmOfStorage || 0,
        cumulativeCashFlow: isNPV
          ? item.cumulativeNpvOfFreeCashFlowToFirmOfStorage || 0
          : item.cumulativeFreeCashFlowToFirmOfStorage || 0,
        capex: isNPV
          ? item.netCapexStorageOnly || 0
          : item.capexStorageOnly || 0,
      },
      'storage-freeCashFlowToEquity': {
        netCashFlow: isNPV
          ? item.npvOfFreeCashFlowToEquityStorage || 0
          : item.freeCashFlowToEquityStorage || 0,
        cumulativeCashFlow: isNPV
          ? item.cumulativeNpvOfFreeCashFlowToEquityStorage || 0
          : item.cumulativeFreeCashFlowToEquityStorage || 0,
        capex: isNPV
          ? item.netCapexStorageOnly || 0
          : item.capexStorageOnly || 0,
      },
    };

    const key = `${storageSelection}-${cashFlowType}`;
    return dataMap[key] || dataMap['withStorage-freeCashFlowToFirm'];
  };

  const getDiscountedPaybackPeriod = () => {
    const paybackPeriodMap = {
      withStorage: simulationData?.result.discountedPaybackPeriodProject,
      withoutStorage: simulationData?.result.discountedPaybackPeriodProjectWos,
      storage: simulationData?.result.discountedPaybackPeriodProjectStorage,
    };

    return paybackPeriodMap[storageSelection as keyof typeof paybackPeriodMap];
  };

  const toggleClosedDataKeys = (key: string = '') => {
    if (closedDataKeys.includes(key)) {
      setClosedDataKeys(closedDataKeys.filter((item) => item !== key));
    } else {
      setClosedDataKeys([...closedDataKeys, key]);
    }
  };

  const data =
    financialData?.graphData
      .slice()
      .sort((a, b) => a.year - b.year)
      .map((item, index) => {
        const storageData = getStorageSpecificData(item);
        return {
          id: index,
          name: item.year,
          totalCapex: storageData.capex.toFixed(0),
          netCashFlow: storageData.netCashFlow.toFixed(0),
          cumulativeCashFlow: storageData.cumulativeCashFlow.toFixed(0),
        };
      }) || [];

  const netCashFlowValues = data.map((item) => Number(item.netCashFlow));
  const minNetCashFlow = Math.min(...netCashFlowValues);
  const maxNetCashFlow = Math.max(...netCashFlowValues);

  const roundToNearest = (num: number, nearest: number) => {
    return Math.round(num / nearest) * nearest;
  };

  const tickStep = Math.max(
    getTickStep(Math.max(Math.abs(minNetCashFlow), Math.abs(maxNetCashFlow))),
    getTickStep(
      Math.max(...data.map((item) => Number(item.cumulativeCashFlow)))
    )
  );
  const upperBound = roundToNearest(maxNetCashFlow * 1.33, tickStep);
  const lowerBound = roundToNearest(minNetCashFlow * 1.33, tickStep);
  const maxAbsBound = Math.max(
    Math.abs(upperBound),
    Math.abs(lowerBound),
    Math.abs(Math.max(...data.map((item) => Number(item.cumulativeCashFlow))))
  );
  const domain = [-maxAbsBound, maxAbsBound];

  const step = maxAbsBound / 3;
  const ticks = [
    -3 * step,
    -2 * step,
    -1 * step,
    0,
    1 * step,
    2 * step,
    3 * step,
  ]
    .map((value) => roundToNearest(value, tickStep))
    .filter(
      (value) => value !== 0 || maxNetCashFlow !== 0 || minNetCashFlow !== 0
    );

  const handleSwitchChange = (index: number) => {
    // Index 0: Nominal, Index 1: NPV
    const switchValues = ['Nominal', 'NPV'];
    setSelectedSwitch(switchValues[index]);
  };

  const toggleLabels = ['Nominal', 'NPV'];

  return (
    <StyledCashFlow>
      <Card
        title={
          <Flex gap={8} align="center">
            <Typography.Title level={4} className="fs-17-bold">
              {t('result.finance.cashFlow.title')}
            </Typography.Title>
            <RTooltip
              title={t('result.finance.cashFlow.title')}
              description={t('result.finance.cashFlow.tooltip')}
            >
              <IconInfo />
            </RTooltip>
          </Flex>
        }
        extra={
          <Flex gap={16} align="center">
            <motion.div
              whileTap={{ scale: 1.1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            >
              <Select
                placeholder={t(
                  'result.finance.cashFlow.cashFlowTypeSelection.placeholder'
                )}
                style={{ minWidth: '200px', width: '200px' }}
                value={cashFlowType}
                onChange={handleCashFlowTypeSelection}
              >
                <Select.Option value="freeCashFlowToFirm">
                  {t(
                    'result.finance.cashFlow.cashFlowTypeSelection.freeCashFlowToFirm'
                  )}
                </Select.Option>
                <Select.Option value="freeCashFlowToEquity">
                  {t(
                    'result.finance.cashFlow.cashFlowTypeSelection.freeCashFlowToEquity'
                  )}
                </Select.Option>
              </Select>
            </motion.div>
            <motion.div
              whileTap={{ scale: 1.1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            >
              <Select
                placeholder={t(
                  'result.finance.cashFlow.storageSelection.placeholder'
                )}
                style={{ minWidth: '150px', width: '150px' }}
                value={storageSelection}
                onChange={handleStorageSelection}
              >
                <Select.Option value="withStorage">
                  {t('result.finance.cashFlow.storageSelection.withStorage')}
                </Select.Option>
                <Select.Option value="withoutStorage">
                  {t('result.finance.cashFlow.storageSelection.withoutStorage')}
                </Select.Option>
                <Select.Option value="storage">
                  {t('result.finance.cashFlow.storageSelection.storage')}
                </Select.Option>
              </Select>
            </motion.div>
            <RToggleButtons
              labels={toggleLabels}
              onButtonClick={handleSwitchChange}
              initialActiveIndex={activeButton}
            />
            <Button icon={<IconDownload fill={theme.colors.bluePrimary} />} />
          </Flex>
        }
        $paddingBody="24px"
        styles={{ header: { padding: '14px 24px' } }}
        style={{ marginTop: '24px', width: '100%' }}
      >
        {isLoading ? (
          <Flex justify="center" align="center" style={{ height: 400 }}>
            <Spin style={{ margin: '20px' }} />
          </Flex>
        ) : error || isDataZero ? (
          <Flex
            vertical
            gap={12}
            justify="center"
            align="center"
            style={{ height: 400 }}
          >
            <IconError width={50} height={50} fill={theme.colors.tagFailure} />
            {error && (
              <Typography.Text className="fs-14-regular text-gray-color text-center">
                {t('result.error.fetchDataError')}
              </Typography.Text>
            )}
            {isDataZero && (
              <Typography.Text className="fs-14-regular text-gray-color text-center">
                {t('result.finance.cashFlow.noDataAvailable')}
              </Typography.Text>
            )}
          </Flex>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={data} stackOffset="sign">
                <CartesianGrid vertical={false} horizontal={true} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                <YAxis
                  domain={domain}
                  tickFormatter={(value) =>
                    tickFormatter(value, currencySymbol)
                  }
                  ticks={ticks}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    const typedPayload = payload as Payload<number, string>[];
                    return (
                      <CustomGraphicTooltip
                        active={active}
                        payload={typedPayload}
                        label={label}
                        extraData={[
                          {
                            name: t(
                              'result.finance.cashFlow.tooltipData.investmentCost'
                            ),
                            value: (
                              <Typography.Text className="text-gray-color">
                                {formatNumberWithCommas(
                                  Number(
                                    data.find((d) => d.name === label)
                                      ?.totalCapex
                                  )
                                ) || 0}
                              </Typography.Text>
                            ),
                            unit: currencySymbol,
                            color: theme.colors.yellowSecondary,
                            type: 'circle',
                          },
                        ]}
                      />
                    );
                  }}
                />

                <Legend
                  iconSize={10}
                  payload={[
                    {
                      value: t('result.finance.cashFlow.legend.netCashFlow'),
                      id: 'netCashFlow',
                      type: 'circle',
                      color: theme.colors.brownDark,
                      inactive: closedDataKeys.includes('netCashFlow'),
                    },
                    {
                      value: t(
                        'result.finance.cashFlow.legend.accumulatedCashFlow'
                      ),
                      id: 'cumulativeCashFlow',
                      type: 'line',
                      color: theme.colors.blueLight,
                      inactive: closedDataKeys.includes('cumulativeCashFlow'),
                    },
                  ]}
                  onClick={({ id }) => toggleClosedDataKeys(id)}
                />
                {!closedDataKeys.includes('cumulativeCashFlow') && (
                  <Line
                    dot={(props: { cy: number; cx: number; index: number }) => {
                      const { cy, cx, index } = props;
                      if (index === 0 || index === data.length - 1) {
                        return (
                          <circle
                            key={`circle-${index}`}
                            cx={cx}
                            cy={cy}
                            r={5}
                            fill={theme.colors.blueLight}
                          />
                        );
                      }
                      return <Fragment key={`dot-${index}`} />;
                    }}
                    name={t(
                      'result.finance.cashFlow.legend.accumulatedCashFlow'
                    )}
                    unit={currencySymbol}
                    type="natural"
                    dataKey="cumulativeCashFlow"
                    stroke={theme.colors.blueLight}
                  />
                )}
                {!closedDataKeys.includes('netCashFlow') && (
                  <Bar
                    name={t('result.finance.cashFlow.legend.netCashFlow')}
                    unit={currencySymbol}
                    dataKey="netCashFlow"
                    barSize={barWidth}
                    stackId="a"
                    fill={theme.colors.brownDark}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>

            <div
              style={{ position: 'absolute', right: '28px', bottom: '28px' }}
            >
              <Typography.Text className="fs-12-bold">
                <span className="fs-12-extra-bold">
                  {t('result.finance.cashFlow.discountedPaybackPeriod', {
                    years: getDiscountedPaybackPeriod(),
                  })}
                </span>
              </Typography.Text>
            </div>
          </>
        )}
      </Card>
    </StyledCashFlow>
  );
};
export default CashFlow;
